import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, ClipboardList, Pencil, Send, Eye, MousePointerClick, Database, UserX, BarChart3, CheckCheck, ExternalLink, Mail, MessageSquare } from 'lucide-react'
import { useReportePorCampania, useReporteWhatsAppPorCampania, useScoreWhatsApp } from '@/modules/cronograma/hooks/useReportes'
import { useCategorizacion, useCampaniaHtml } from '@/modules/multichannel/hooks/useCampanias'
import { useAuth } from '@/auth/context/AuthContext'
import { useHistorialReporte, useHistorialReporteWhatsApp } from '../hooks/useSeguimientoReportes'
import { SuperMetricCard, GaugeChart } from '@/modules/multichannel/components/ReportCharts'
import { ConversionFunnel } from '@/modules/multichannel/components/ConversionFunnel'
import { ReporteModal } from '@/modules/cronograma/components/ReporteModal'
import { ReporteWhatsAppModal } from '@/modules/cronograma/components/ReporteWhatsAppModal'
import { HistorialTimeline } from '../components/HistorialTimeline'
import type { CampaniaPendiente } from '../types'

const WHATSAPP_TIPOS = ['Whatsapp', 'Email + Whatsapp']
const EMAIL_TIPOS = ['Email', 'Email + Whatsapp']

const BENCHMARKS = {
  entrega: 95,
  aperturas: 25,
  clics: 3,
}


export function SeguimientoDetallePage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const campaniaId = Number(id)

  const { user } = useAuth()

  // Modal state
  const [reporteEmailModalOpen, setReporteEmailModalOpen] = useState(false)
  const [reporteWhatsAppModalOpen, setReporteWhatsAppModalOpen] = useState(false)

  // Fetch campania info
  const { data: campania, isLoading: loadingCampania } = useQuery({
    queryKey: ['seguimiento-reportes', 'campania-info', campaniaId],
    queryFn: async () => {
      const [sinReporte, enRecopilacion] = await Promise.all([
        apiClient.get<CampaniaPendiente[]>(`/multichannel/seguimiento-reportes/campanias/?tab=sin_reporte`),
        apiClient.get<CampaniaPendiente[]>(`/multichannel/seguimiento-reportes/campanias/?tab=en_recopilacion`),
      ])
      return sinReporte.data.find((c) => c.id === campaniaId)
        || enRecopilacion.data.find((c) => c.id === campaniaId)
        || null
    },
    enabled: !!campaniaId,
  })

  const isEmail = campania ? EMAIL_TIPOS.includes(campania.tipo) : false
  const isWhatsApp = campania ? WHATSAPP_TIPOS.includes(campania.tipo) : false

  // Reportes
  const { data: reporte } = useReportePorCampania(campaniaId, isEmail)
  const { data: reporteWA } = useReporteWhatsAppPorCampania(campaniaId, isWhatsApp)

  // Categorizacion
  const { data: categorizacionData } = useCategorizacion(
    reporte ? [reporte.campania_id] : []
  )
  const scoreData = categorizacionData?.[String(reporte?.campania_id)]

  // Score WhatsApp (incluye benchmarks del backend)
  const { data: scoreWA } = useScoreWhatsApp(reporteWA?.id || null, !!reporteWA?.id)
  const benchmarkEntregaWA = scoreWA?.benchmark_entrega ?? undefined
  const benchmarkAperturasWA = scoreWA?.benchmark_aperturas ?? undefined
  const benchmarkClicsWA = scoreWA?.benchmark_clics ?? undefined

  // HTML preview
  const { data: campaniaHtml } = useCampaniaHtml(isEmail ? campaniaId : null)

  // Historial
  const { data: historialEmail } = useHistorialReporte(campaniaId)
  const { data: historialWA } = useHistorialReporteWhatsApp(campaniaId)

  const isLoading = loadingCampania

  const handleEditReporte = () => {
    if (isEmail) setReporteEmailModalOpen(true)
    if (isWhatsApp) setReporteWhatsAppModalOpen(true)
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={() => navigate('/seguimiento-reportes')}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Volver
        </Button>
        {campania?.tiene_reporte && (
          <Button
            onClick={handleEditReporte}
            className="bg-amber-800 hover:bg-amber-900 text-white gap-2"
          >
            <Pencil className="h-4 w-4" />
            Editar Reporte
          </Button>
        )}
        {!campania?.tiene_reporte && campania && (
          <Button
            onClick={handleEditReporte}
            className="bg-amber-800 hover:bg-amber-900 text-white gap-2"
          >
            <BarChart3 className="h-4 w-4" />
            Crear Reporte
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
        </div>
      ) : (
        <>
          {/* Campaign Info Card */}
          <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-amber-500/15 via-amber-500/5 via-50% to-transparent dark:from-amber-800/20 dark:via-amber-900/10 dark:via-50% dark:to-transparent">
            <CardHeader className="flex flex-row items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="rounded-lg bg-amber-500/10 p-2">
                  <ClipboardList className="h-5 w-5 text-amber-500" />
                </div>
                <div>
                  <CardTitle className="text-lg">
                    {campania?.nombre_campania || `Campana #${campaniaId}`}
                  </CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {campania?.marca_principal_nombre || campania?.marca}
                  </p>
                </div>
              </div>
              <Badge variant="outline" className={
                campania?.tiene_reporte
                  ? "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/30"
                  : "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30"
              }>
                {campania?.tiene_reporte ? 'En Recopilacion' : 'Sin Reporte'}
              </Badge>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <InfoItem label="Tipo" value={campania?.tipo || '-'} />
                <InfoItem label="Formato" value={campania?.formato || '-'} />
                <InfoItem
                  label="Fecha Envio"
                  value={
                    campania?.fecha_publicacion
                      ? new Date(campania.fecha_publicacion + 'T00:00:00').toLocaleDateString('es-EC')
                      : '-'
                  }
                />
                <InfoItem
                  label="Dias desde envio"
                  value={String(campania?.dias_desde_envio ?? 0)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Métricas del reporte email */}
          {reporte && (
            <>
              <h2 className="text-lg font-semibold">Metricas del Reporte</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <SuperMetricCard
                  icon={<Database className="h-6 w-6" />}
                  label="Base"
                  count={reporte.base}
                />
                <SuperMetricCard
                  icon={<Send className="h-6 w-6" />}
                  label="Entrega"
                  percentage={reporte.tasa_entrega}
                  count={reporte.entrega}
                  benchmark={BENCHMARKS.entrega}
                  category={reporte.categoria_entrega}
                />
                <SuperMetricCard
                  icon={<Eye className="h-6 w-6" />}
                  label="Aperturas"
                  percentage={reporte.tasa_aperturas}
                  count={reporte.aperturas_unicas}
                  benchmark={BENCHMARKS.aperturas}
                  category={reporte.categoria_aperturas}
                />
                <SuperMetricCard
                  icon={<MousePointerClick className="h-6 w-6" />}
                  label="Clics"
                  percentage={reporte.tasa_clics}
                  count={reporte.clics_unicos}
                  benchmark={BENCHMARKS.clics}
                  category={reporte.categoria_clics}
                />
                <SuperMetricCard
                  icon={<UserX className="h-6 w-6" />}
                  label="Unsub"
                  percentage={reporte.tasa_unsub}
                  count={reporte.unsub}
                />
              </div>

              {/* Funnel */}
              <ConversionFunnel
                base={reporte.base}
                entrega={reporte.entrega}
                aperturas={reporte.aperturas_unicas}
                clics={reporte.clics_unicos}
                tasa_entrega={reporte.tasa_entrega}
                tasa_aperturas={reporte.tasa_aperturas}
                tasa_clics={reporte.tasa_clics}
                calificacion={scoreData?.calificacion || null}
              />

              {/* Gauges */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <GaugeChart
                  value={reporte.tasa_entrega}
                  benchmark={BENCHMARKS.entrega}
                  label="Entrega"
                  category={reporte.categoria_entrega}
                  icon={<Send className="h-4 w-4" />}
                />
                <GaugeChart
                  value={reporte.tasa_aperturas}
                  benchmark={BENCHMARKS.aperturas}
                  label="Aperturas"
                  category={reporte.categoria_aperturas}
                  icon={<Eye className="h-4 w-4" />}
                />
                <GaugeChart
                  value={reporte.tasa_clics}
                  benchmark={BENCHMARKS.clics}
                  label="Clics"
                  category={reporte.categoria_clics}
                  icon={<MousePointerClick className="h-4 w-4" />}
                />
              </div>
            </>
          )}

          {/* Métricas del reporte WhatsApp */}
          {reporteWA && (
            <>
              <h2 className="text-lg font-semibold">Metricas del Reporte WhatsApp</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                <SuperMetricCard
                  icon={<Database className="h-6 w-6" />}
                  label="Base"
                  count={reporteWA.base}
                />
                <SuperMetricCard
                  icon={<Send className="h-6 w-6" />}
                  label="Entrega"
                  percentage={reporteWA.tasa_entrega}
                  count={reporteWA.entrega}
                  benchmark={benchmarkEntregaWA}
                  category={reporteWA.categoria_entrega}
                />
                <SuperMetricCard
                  icon={<Eye className="h-6 w-6" />}
                  label="Leidos"
                  percentage={reporteWA.tasa_aperturas}
                  count={reporteWA.leidos}
                  benchmark={benchmarkAperturasWA}
                  category={reporteWA.categoria_aperturas}
                />
                <SuperMetricCard
                  icon={<MousePointerClick className="h-6 w-6" />}
                  label="Clics"
                  percentage={reporteWA.tasa_clics}
                  count={reporteWA.clics}
                  benchmark={benchmarkClicsWA}
                  category={reporteWA.categoria_clics}
                />
                <SuperMetricCard
                  icon={<UserX className="h-6 w-6" />}
                  label="Unsub"
                  percentage={reporteWA.tasa_unsub}
                  count={reporteWA.unsub}
                />
              </div>

              {/* Funnel WA */}
              <ConversionFunnel
                base={reporteWA.base}
                entrega={reporteWA.entrega}
                aperturas={reporteWA.leidos}
                clics={reporteWA.clics}
                tasa_entrega={reporteWA.tasa_entrega}
                tasa_aperturas={reporteWA.tasa_aperturas}
                tasa_clics={reporteWA.tasa_clics}
              />

              {/* Gauges WA */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <GaugeChart
                  value={reporteWA.tasa_entrega}
                  benchmark={benchmarkEntregaWA ?? 0}
                  label="Entrega"
                  category={reporteWA.categoria_entrega ?? null}
                  icon={<Send className="h-4 w-4" />}
                />
                <GaugeChart
                  value={reporteWA.tasa_aperturas}
                  benchmark={benchmarkAperturasWA ?? 0}
                  label="Leidos"
                  category={reporteWA.categoria_aperturas ?? null}
                  icon={<Eye className="h-4 w-4" />}
                />
                <GaugeChart
                  value={reporteWA.tasa_clics}
                  benchmark={benchmarkClicsWA ?? 0}
                  label="Clics"
                  category={reporteWA.categoria_clics ?? null}
                  icon={<MousePointerClick className="h-4 w-4" />}
                />
              </div>
            </>
          )}

          {/* Previsualizacion del contenido */}
          {campaniaHtml?.contenido_html && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <Mail className="h-5 w-5 text-amber-500" />
                Vista Previa del Email
              </h2>
              <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden">
                <CardContent className="p-0">
                  <iframe
                    srcDoc={campaniaHtml.contenido_html}
                    sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
                    className="w-full border-0"
                    style={{ height: '500px' }}
                    title={`Preview de ${campania?.nombre_campania || 'email'}`}
                  />
                </CardContent>
              </Card>
            </div>
          )}

          {reporteWA && (reporteWA.mensaje_texto || reporteWA.imagen_url) && (
            <div className="space-y-3">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-amber-500" />
                Vista Previa del Mensaje WhatsApp
              </h2>
              <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden">
                <CardContent className="p-0">
                  <div
                    className="p-6 flex justify-center"
                    style={{ backgroundColor: '#efeae2' }}
                  >
                    <div className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-sm" style={{ maxWidth: '320px', width: '100%' }}>
                      {reporteWA.imagen_url && (
                        <div className="px-1.5 pt-1.5">
                          <img
                            src={reporteWA.imagen_url}
                            alt="Imagen del mensaje"
                            className="w-full h-auto max-h-[300px] object-cover rounded-lg"
                          />
                        </div>
                      )}
                      {(reporteWA.mensaje_texto || reporteWA.footer) && (
                        <div className="px-3 py-2">
                          {reporteWA.mensaje_texto && (
                            <p
                              className="text-[13px] whitespace-pre-wrap text-gray-900 dark:text-gray-100 leading-snug"
                              dangerouslySetInnerHTML={{
                                __html: reporteWA.mensaje_texto
                                  .replace(/\{\{nombre\}\}/gi, user?.first_name || 'Usuario')
                                  .replace(/\{nombre\}/gi, user?.first_name || 'Usuario')
                                  .replace(/\*([^*]+)\*/g, '<strong>$1</strong>')
                              }}
                            />
                          )}
                          {reporteWA.footer && (
                            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">{reporteWA.footer}</p>
                          )}
                          <div className="flex items-center justify-end gap-1 mt-1">
                            <span className="text-[10px] text-gray-500">10:30 a.m.</span>
                            <CheckCheck className="h-3.5 w-3.5 text-blue-500" />
                          </div>
                        </div>
                      )}
                      {reporteWA.botones_cta && reporteWA.botones_cta.length > 0 && (
                        <div className="border-t border-gray-200 dark:border-gray-700">
                          {reporteWA.botones_cta.map((boton: { texto: string; url: string }, index: number) => (
                            <a
                              key={index}
                              href={boton.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`flex items-center justify-center gap-2 px-3 py-2.5 text-[13px] font-medium text-[#00a884] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors ${
                                index < reporteWA.botones_cta!.length - 1 ? 'border-b border-gray-200 dark:border-gray-700' : ''
                              }`}
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                              {boton.texto}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Sin reporte */}
          {!reporte && !reporteWA && !isLoading && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardContent className="py-12 text-center text-muted-foreground">
                Esta campana aun no tiene reporte. Crea uno para ver metricas y graficas.
              </CardContent>
            </Card>
          )}

          {/* Historial de Ediciones */}
          {((historialEmail && historialEmail.length > 0) || (historialWA && historialWA.length > 0)) && (
            <>
              <h2 className="text-lg font-semibold">Historial de Ediciones</h2>
              {historialEmail && historialEmail.length > 0 && (
                <>
                  {isWhatsApp && <h3 className="text-sm font-medium text-muted-foreground">Email</h3>}
                  <HistorialTimeline historial={historialEmail} tipo="email" />
                </>
              )}
              {historialWA && historialWA.length > 0 && (
                <>
                  {isEmail && <h3 className="text-sm font-medium text-muted-foreground">WhatsApp</h3>}
                  <HistorialTimeline historial={historialWA} tipo="whatsapp" />
                </>
              )}
            </>
          )}
        </>
      )}

      {/* Modales de Reporte */}
      {campania && (
        <ReporteModal
          open={reporteEmailModalOpen}
          onClose={() => setReporteEmailModalOpen(false)}
          campaniaId={campania.id}
          nombreCampania={campania.nombre_campania}
          tieneReporte={campania.tiene_reporte}
        />
      )}
      {campania && (
        <ReporteWhatsAppModal
          open={reporteWhatsAppModalOpen}
          onClose={() => setReporteWhatsAppModalOpen(false)}
          campaniaId={campania.id}
          nombreCampania={campania.nombre_campania}
          nombreLaboratorio={campania.marca_principal_nombre || campania.marca}
          fechaPublicacion={campania.fecha_publicacion}
          tieneReporte={campania.tiene_reporte}
        />
      )}
    </div>
  )
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  )
}
